import React, { useState, useCallback, useEffect } from "react";
import {
  Page,
  Card,
  BlockStack,
  Button,
  Text,
  Grid,
  List,
  Badge,
  Toast,
  Box,
} from "@shopify/polaris";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ApiCall, GetApiCall } from "../helper/axios";
import { BridgeApp } from "../helper/commonApi";
import { Redirect } from "@shopify/app-bridge/actions";

export default function Pricing() {
  const location = useLocation();
  const navigate = useNavigate();
  const { store_data } = useSelector((store) => store.commonData);
  const token = store_data?.token;

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastError, setToastError] = useState(false);

  const showToast = useCallback((message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  }, []);

  useEffect(() => {
    const fetchPlanStatus = async () => {
      if (!token) return;
      try {
        const headers = {
          authentication: token,
          "Content-Type": "application/json",
        };
        const response = await GetApiCall("GET", "/plan/status", headers);
        if (response?.data?.data?.hasPlan) {
          const plan = response?.data?.data?.plan;
          setCurrentPlan(plan);
          if (plan?.plan_type) {
            setSelectedPlan(plan.plan_type);
          }
        }
      } catch (error) {
        console.error("Error fetching plan status:", error);
      }
    };

    fetchPlanStatus();
  }, [token]);

  const handlePlanSubmit = useCallback(async (planTypeOverride = null) => {
    const planType = planTypeOverride || selectedPlan;
    if (!planType) {
      showToast("Please select a plan", true);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        plan_type: planType,
        plan_name: planType === "free" ? "Free Plan" : "Paid Plan",
        plan_price: planType === "free" ? 0 : 29.99,
      };

      const response = await ApiCall(
        "POST",
        "/plan/select",
        payload,
        { authentication: token }
      );

      if (response?.data?.status === "success") {
        const confirmationUrl = response?.data?.data?.confirmationUrl;
        if (confirmationUrl) {
          if (process.env?.MODE !== "local" && BridgeApp) {
            const redirect = Redirect.create(BridgeApp);
            redirect.dispatch(Redirect.Action.REMOTE, confirmationUrl);
          } else {
            window.location.href = confirmationUrl;
          }
          return;
        }

        if (planType === "paid") {
          showToast("Plan selected successfully", false);
        } else {
          showToast("Free plan selected successfully", false);
        }

        // Update UI immediately so the user sees "Current plan" without manual refresh
        try {
          const headers = {
            authentication: token,
            "Content-Type": "application/json",
          };
          const statusRes = await GetApiCall("GET", "/plan/status", headers);
          if (statusRes?.data?.data?.hasPlan && statusRes?.data?.data?.plan) {
            const plan = statusRes.data.data.plan;
            setCurrentPlan(plan);
            if (plan?.plan_type) setSelectedPlan(plan.plan_type);
          } else {
            setCurrentPlan({
              plan_type: planType,
              plan_name: planType === "free" ? "Free Plan" : "Paid Plan",
              plan_price: planType === "free" ? 0 : 29.99,
            });
            setSelectedPlan(planType);
          }
        } catch (_) {
          setCurrentPlan({
            plan_type: planType,
            plan_name: planType === "free" ? "Free Plan" : "Paid Plan",
            plan_price: planType === "free" ? 0 : 29.99,
          });
          setSelectedPlan(planType);
        }

        // Clear plan cache so PlanGuard on home will refetch and see hasPlan: true
        try {
          sessionStorage.removeItem("plan_status");
        } catch (_) {}

        window.location.reload();
        // Client-side redirect to home so the app stays mounted and home shows with updated plan
        navigate("/", { replace: true });
      } else {
        showToast(response?.data?.message || "Failed to select plan", true);
      }
    } catch (error) {
      console.error("Error selecting plan:", error);
      showToast("Failed to select plan. Please try again.", true);
    } finally {
      setIsLoading(false);
    }
  }, [selectedPlan, token, showToast, navigate]);

  const handlePlanSelect = useCallback((planType) => {
    if (currentPlan?.plan_type === planType) {
      return;
    }

    setSelectedPlan(planType);
    handlePlanSubmit(planType);
  }, [currentPlan?.plan_type, handlePlanSubmit]);

  const plans = [
    {
      id: "free",
      name: "Free",
      price: "$0",
      priceValue: 0,
      description: "Perfect for getting started",
      features: [
        "Create up to 1 pricing rule",
        "Add up to 3 products per rule",
        "Basic pricing features",
        "Email support",
      ],
      popular: false,
    },
    {
      id: "paid",
      name: "Pro",
      price: "$29.99",
      priceValue: 29.99,
      description: "For growing businesses",
      features: [
        "Unlimited pricing rules",
        "Unlimited products per rule",
        "All pricing features",
        "Priority email support",
        "Advanced analytics",
      ],
      popular: true,
    },
  ];

  const toastMarkup = toastActive ? (
    <Toast
      content={toastMessage}
      error={toastError}
      onDismiss={() => setToastActive(false)}
    />
  ) : null;

  return (
    <Page>
      <BlockStack gap="600">
        <Box paddingBlockStart="400">
          <Text variant="headingLg" as="h1">
            Select a plan
          </Text>
        </Box>

        <Grid>
          {plans.map((plan) => (
            <Grid.Cell
              key={plan.id}
              columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}
            >
              <Card>
                <BlockStack gap="400">
                  {/* Plan Name */}
                  <Text variant="headingSm" as="h3">
                    {plan.name}
                  </Text>

                  {/* Price */}
                  <Text variant="heading2xl" as="h2">
                    {plan.price}
                  </Text>
                  {plan.priceValue > 0 && (
                    <Text variant="bodySm" color="subdued">
                      per month
                    </Text>
                  )}

                  {/* Current Plan Badge */}
                  {currentPlan?.plan_type === plan.id && (
                    <Badge tone="info">Current plan</Badge>
                  )}

                  {/* Select Button */}
                  <Box paddingBlockStart="200">
                    <Button
                      onClick={() => handlePlanSelect(plan.id)}
                      disabled={
                        isLoading ||
                        currentPlan?.plan_type === plan.id
                      }
                      fullWidth
                      variant="primary"
                      // style={{
                      //   backgroundColor: "#000000",
                      //   color: "#ffffff",
                      //   border: "none",
                      // }}
                    >
                      {currentPlan?.plan_type === plan.id
                        ? "Current plan"
                        : currentPlan?.plan_type === "paid" && plan.id === "free"
                          ? "Downgrade to Free"
                          : "Select"}
                    </Button>
                  </Box>

                  {/* Features List */}
                  <Box paddingBlockStart="400">
                    <List type="bullet">
                      {plan.features.map((feature, index) => (
                        <List.Item key={index}>
                          <Text variant="bodyMd">{feature}</Text>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                </BlockStack>
              </Card>
            </Grid.Cell>
          ))}
        </Grid>
      </BlockStack>

      {toastMarkup}
    </Page>
  );
}
